import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { analyticsService } from '../lib/services/analytics-service';
import { cookieService } from '../lib/services/cookie-service';

interface GDPRSettings {
  gdprAccepted: boolean;
  locationConsent: boolean;
  marketingConsent: boolean;
  analyticsConsent: boolean;
  acceptedAt?: string;
}

interface GDPRContextType {
  gdprSettings: GDPRSettings | null;
  showGDPRModal: boolean;
  acceptGDPR: (settings: Partial<GDPRSettings>) => Promise<void>;
  updateGDPRSettings: (settings: Partial<GDPRSettings>) => Promise<void>;
  loading: boolean;
}

const GDPRContext = createContext<GDPRContextType | undefined>(undefined);

export function useGDPR() {
  const context = useContext(GDPRContext);
  if (context === undefined) {
    throw new Error('useGDPR must be used within a GDPRProvider');
  }
  return context;
}

interface GDPRProviderProps {
  children: React.ReactNode;
}

export function GDPRProvider({ children }: GDPRProviderProps) {
  const { user } = useAuth();
  const [gdprSettings, setGdprSettings] = useState<GDPRSettings | null>(null);
  const [showGDPRModal, setShowGDPRModal] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initialize cookie service
  useEffect(() => {
    cookieService.initialize();
  }, []);

  // Initialize analytics when consent changes
  useEffect(() => {
    if (gdprSettings) {
      analyticsService.initialize(gdprSettings.analyticsConsent);
      
      // Update cookie consent
      cookieService.setConsentStatus({
        essential: true,
        analytics: gdprSettings.analyticsConsent,
        marketing: gdprSettings.marketingConsent,
        functional: gdprSettings.locationConsent // Using location consent for functional cookies
      });
    }
  }, [gdprSettings]);

  // Load GDPR settings on user change
  useEffect(() => {
    if (user) {
      loadGDPRSettings();
    } else {
      // For non-authenticated users, check localStorage
      const localSettings = localStorage.getItem('gdpr_settings');
      if (localSettings) {
        try {
          const settings = JSON.parse(localSettings);
          setGdprSettings(settings);
          // Only show modal if GDPR was never accepted
          setShowGDPRModal(!settings.gdprAccepted);
        } catch (error) {
          console.error('Error parsing local GDPR settings:', error);
          setShowGDPRModal(true);
        }
      } else {
        setShowGDPRModal(true);
      }
      setLoading(false);
    }
  }, [user]);

  const loadGDPRSettings = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Check localStorage first to avoid showing modal during login transition
      const localSettings = localStorage.getItem('gdpr_settings');
      let localGdprSettings = null;
      
      if (localSettings) {
        try {
          localGdprSettings = JSON.parse(localSettings);
          // If user already accepted locally, don't show modal
          if (localGdprSettings.gdprAccepted) {
            setGdprSettings(localGdprSettings);
            setShowGDPRModal(false);
          }
        } catch (parseError) {
          console.warn('Error parsing local GDPR settings:', parseError);
        }
      }
      
      // Add timeout to prevent gateway timeout issues
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );

      const dbPromise = supabase
        .from('users')
        .select('gdpr_settings')
        .eq('id', user.id)
        .single();

      const { data, error } = await Promise.race([dbPromise, timeoutPromise]) as any;

      if (error) {
        // If database fails, use localStorage as fallback
        console.warn('Database GDPR load failed, using localStorage:', error);
        if (localGdprSettings) {
          setGdprSettings(localGdprSettings);
          setShowGDPRModal(!localGdprSettings.gdprAccepted);
          
          // Try to sync localStorage to database in background
          try {
            await supabase
              .from('users')
              .update({ gdpr_settings: localGdprSettings })
              .eq('id', user.id);
          } catch (syncError) {
            console.warn('Failed to sync GDPR settings to database:', syncError);
          }
        } else {
          setShowGDPRModal(true);
        }
        return;
      }

      const settings = data?.gdpr_settings || null;
      
      if (settings) {
        setGdprSettings(settings);
        // Don't show modal if user has already accepted GDPR
        setShowGDPRModal(!settings.gdprAccepted);
        
        // Sync with localStorage for offline access
        localStorage.setItem('gdpr_settings', JSON.stringify(settings));
      } else {
        // No settings in database, check localStorage
        if (localGdprSettings) {
          setGdprSettings(localGdprSettings);
          setShowGDPRModal(!localGdprSettings.gdprAccepted);
          
          // Sync localStorage settings to database
          try {
            await supabase
              .from('users')
              .update({ gdpr_settings: localGdprSettings })
              .eq('id', user.id);
          } catch (syncError) {
            console.warn('Failed to sync GDPR settings to database:', syncError);
          }
        } else {
          setShowGDPRModal(true);
        }
      }
    } catch (error) {
      console.error('Error loading GDPR settings:', error);
      // Fallback to localStorage
      const localSettings = localStorage.getItem('gdpr_settings');
      if (localSettings) {
        try {
          const settings = JSON.parse(localSettings);
          setGdprSettings(settings);
          setShowGDPRModal(!settings.gdprAccepted);
        } catch (parseError) {
          setShowGDPRModal(true);
        }
      } else {
        setShowGDPRModal(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const acceptGDPR = async (newSettings: Partial<GDPRSettings>) => {
    const settings: GDPRSettings = {
      gdprAccepted: true,
      locationConsent: false,
      marketingConsent: false,
      analyticsConsent: false,
      ...newSettings,
      acceptedAt: new Date().toISOString()
    };

    try {
      // Always save to localStorage first for immediate feedback
      localStorage.setItem('gdpr_settings', JSON.stringify(settings));
      setGdprSettings(settings);
      setShowGDPRModal(false);

      if (user) {
        // Save to database for authenticated users with timeout
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        );

        const dbPromise = supabase
          .from('users')
          .update({ gdpr_settings: settings })
          .eq('id', user.id);

        try {
          const { error } = await Promise.race([dbPromise, timeoutPromise]) as any;
          if (error) {
            console.warn('Failed to save GDPR to database, but saved locally:', error);
          }
        } catch (dbError) {
          console.warn('Database save timeout, but settings saved locally:', dbError);
        }
      }
    } catch (error) {
      console.error('Error saving GDPR settings:', error);
      throw error;
    }
  };

  const updateGDPRSettings = async (newSettings: Partial<GDPRSettings>) => {
    if (!gdprSettings) return;

    const updatedSettings = { 
      ...gdprSettings, 
      ...newSettings,
      // Ensure gdprAccepted remains true when updating
      gdprAccepted: true,
      acceptedAt: gdprSettings.acceptedAt || new Date().toISOString()
    };

    try {
      // Always save to localStorage first
      localStorage.setItem('gdpr_settings', JSON.stringify(updatedSettings));
      setGdprSettings(updatedSettings);

      if (user) {
        // Save to database with timeout
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        );

        const dbPromise = supabase
          .from('users')
          .update({
            gdpr_settings: updatedSettings
          })
          .eq('id', user.id);

        try {
          const { error } = await Promise.race([dbPromise, timeoutPromise]) as any;
          if (error) {
            console.warn('Failed to update GDPR in database, but saved locally:', error);
          }
        } catch (dbError) {
          console.warn('Database update timeout, but settings saved locally:', dbError);
        }
      }
    } catch (error) {
      console.error('Error updating GDPR settings:', error);
      throw error;
    }
  };

  const value: GDPRContextType = {
    gdprSettings,
    showGDPRModal,
    acceptGDPR,
    updateGDPRSettings,
    loading
  };

  return (
    <GDPRContext.Provider value={value}>
      {children}
    </GDPRContext.Provider>
  );
}
